/**
 * BottleCountOverlay — the visual bottle-fraction counter.
 *
 * Liquor and wine counts are whole bottles plus an eyeball estimate of the
 * open bottle in hand. This overlay is that estimate made tangible: the count
 * tool "flips" to its back side, which shows the bottle silhouette, and the
 * counter drags from the base of the glass up to the fill level they see.
 * The slider snaps to tenths; the fluid never rises past the shoulder into
 * the neck, so 10/10 is a full bottle; the liquid is an anonymous gray
 * because nobody should have to tell us what color their rye is.
 *
 * Opened on request only, from the count card's bottle button — a typed
 * decimal never summons it. Done replaces the DECIMAL portion of the count
 * (5 + 0.7 → "5.7"); the whole bottles the counter already entered are kept.
 *
 * Pure presentation: the parent owns the count value and the navigation.
 *
 * Props:
 *   itemName      - heading context (the bottle in hand)
 *   unitLabel     - the item's count unit, shown beside the number
 *   wholePart     - whole bottles already counted (display only)
 *   initialTenths - starting fill level, 0–10
 *   mobile        - full-screen sheet vs centered desktop panel
 *   onDone        - (tenths) => void — apply + close (plays the flip-out first)
 *   onCancel      - () => void — close without changing anything
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { BOTTLE_TENTHS_MAX, formatBottleCount } from '../utils/bottleCounting.js';

// Geometry of public/images/bottle-count/bottle-outline.png, measured by
// scripts/make-bottle-count-assets.py (see its JSON output). The fluid spans
// the bottle BODY only — glass base to shoulder line.
const BODY_TOP_FRAC = 0.421; // shoulder line, as a fraction of image height
const BODY_BOTTOM_FRAC = 0.9451; // glass base, as a fraction of image height
const BOTTLE_ASPECT = 469 / 601; // width / height of the bottle art
const OUTLINE_URL = '/images/bottle-count/bottle-outline.png';
const MASK_URL = '/images/bottle-count/bottle-fill-mask.png';

const RAIL_W = 40; // px column for each side rail (hashes + decimal labels)
const RAIL_GAP = 8; // px between the bottle box and each rail column
const FLIP_MS = 300;

const C = {
  ink: '#3D2518',
  body: '#5A4A42',
  muted: '#8B7B73',
  faint: '#A89890',
  line: '#C8B5A0',
  hairline: '#E8DDD0',
  surface: '#FEFCFA',
  fluidTop: '#B7BCC2',
  fluidBottom: '#64696F',
};

// Injected keyframes — the count tool has no other 3-D animation, so this
// lives beside the component that owns it. Reduced-motion users get no flip.
const FLIP_CSS = `
@keyframes bottleCountFlipIn {
  from { transform: rotateY(-86deg); opacity: 0.35; }
  to { transform: rotateY(0deg); opacity: 1; }
}
@keyframes bottleCountFlipOut {
  from { transform: rotateY(0deg); opacity: 1; }
  to { transform: rotateY(86deg); opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .bottle-count-flip { animation: none !important; }
}
`;

const clampTenths = (t) => Math.max(0, Math.min(BOTTLE_TENTHS_MAX, t));
const levelTopFrac = (tenths) =>
  BODY_BOTTOM_FRAC - (tenths / BOTTLE_TENTHS_MAX) * (BODY_BOTTOM_FRAC - BODY_TOP_FRAC);

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const buzz = () => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(6);
  } catch { /* haptics are a nicety, never a failure */ }
};

/**
 * The little bottle icon on the count card's bottle tab. Strokes only, so it
 * inherits the button's text color — same visual language as the backspace
 * icon in the keypad.
 */
export const BottleGlyph = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M10 2h4v3h-4z" />
    <path d="M10 5c0 1.6-1.4 2-2.2 3.1-.5.7-.8 1.6-.8 2.4V19a2.5 2.5 0 0 0 2.5 2.5h5A2.5 2.5 0 0 0 17 19v-8.5c0-.8-.3-1.7-.8-2.4C15.4 7 14 6.6 14 5" />
  </svg>
);

export default function BottleCountOverlay({
  itemName = 'Item',
  unitLabel = 'bottle',
  wholePart = 0,
  initialTenths = 0,
  mobile = false,
  onDone,
  onCancel,
}) {
  const [tenths, setTenths] = useState(() => clampTenths(initialTenths));
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [bottlePx, setBottlePx] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null); // outer measuring box (flex space)
  const wrapRef = useRef(null); // the bottle-sized box the drag reads against
  const leaveTimerRef = useRef(null);
  const sliderRef = useRef(null);

  // The bottle is sized to the larger of the stage's two constraints so it
  // takes the majority of the screen without pushing Done out of reach.
  useLayoutEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      const availW = Math.max(140, el.clientWidth - (RAIL_W + RAIL_GAP) * 2);
      const availH = Math.max(180, el.clientHeight);
      let h = availH;
      let w = h * BOTTLE_ASPECT;
      if (w > availW) {
        w = availW;
        h = w / BOTTLE_ASPECT;
      }
      setBottlePx({ w: Math.floor(w), h: Math.floor(h) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
  }, []);

  // Arrow keys work the moment the sheet opens.
  useEffect(() => {
    sliderRef.current?.focus?.();
  }, []);

  const setFromClientY = (clientY) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.height <= 0) return;
    const topY = rect.top + BODY_TOP_FRAC * rect.height;
    const bottomY = rect.top + BODY_BOTTOM_FRAC * rect.height;
    const frac = (bottomY - clientY) / (bottomY - topY);
    const next = clampTenths(Math.round(frac * BOTTLE_TENTHS_MAX));
    setTenths((prev) => {
      if (next !== prev) buzz();
      return next;
    });
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older Safari */ }
    setDragging(true);
    setFromClientY(e.clientY);
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    setFromClientY(e.clientY);
  };
  const endDrag = () => setDragging(false);

  // Flip out, THEN tell the parent — the back of the card rotates away before
  // the keypad comes back, so the two screens never occupy the frame together.
  const finishWith = (callback) => {
    if (leaving) return;
    setLeaving(true);
    const delay = prefersReducedMotion() ? 0 : FLIP_MS;
    leaveTimerRef.current = setTimeout(() => callback?.(), delay);
  };
  const handleDone = () => finishWith(() => onDone?.(tenths));
  const handleCancel = () => finishWith(() => onCancel?.());

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault();
      setTenths((t) => clampTenths(t + 1));
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setTenths((t) => clampTenths(t - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setTenths(BOTTLE_TENTHS_MAX);
    } else if (e.key === 'End') {
      e.preventDefault();
      setTenths(0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleDone();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const displayValue = formatBottleCount(wholePart, tenths);
  const fillTopPct = levelTopFrac(tenths) * 100;
  const ticks = Array.from({ length: BOTTLE_TENTHS_MAX + 1 }, (_, i) => i);

  const rail = (side) => (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [side]: -(RAIL_W + RAIL_GAP),
        width: RAIL_W,
        pointerEvents: 'none',
      }}
    >
      {ticks.map((t) => {
        const top = levelTopFrac(t) * 100;
        const active = t === tenths;
        return (
          <div
            key={t}
            style={{
              position: 'absolute',
              top: `${top}%`,
              [side === 'left' ? 'right' : 'left']: 0,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flexDirection: side === 'left' ? 'row' : 'row-reverse',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: active ? 14 : 8,
                height: active ? 3 : 2,
                borderRadius: 2,
                backgroundColor: active ? C.ink : C.faint,
                transition: 'width 120ms',
              }}
            />
            <span
              style={{
                fontSize: active ? '0.72rem' : '0.58rem',
                fontWeight: active ? 900 : 600,
                color: active ? C.ink : C.faint,
                fontFamily: 'Montserrat, sans-serif',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {(t / 10).toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );

  const panel = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bottle fill level"
      className="bottle-count-flip"
      style={{
        width: mobile ? '100%' : 'min(430px, 94vw)',
        height: mobile ? '100dvh' : 'min(700px, 94vh)',
        background: C.surface,
        borderRadius: mobile ? 0 : 18,
        boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: `${leaving ? 'bottleCountFlipOut' : 'bottleCountFlipIn'} ${FLIP_MS}ms cubic-bezier(0.32, 0.72, 0.35, 1) both`,
        transformStyle: 'preserve-3d',
        backfaceVisibility: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{
        padding: mobile ? 'calc(12px + env(safe-area-inset-top, 0px)) 18px 8px' : '16px 20px 8px',
        textAlign: 'center',
        flex: '0 0 auto',
      }}>
        <div style={{
          fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: C.muted,
        }}>
          Bottle fill level
        </div>
        <div style={{
          fontSize: '0.95rem', fontWeight: 700, color: C.ink, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {itemName}
        </div>
        <div style={{ fontSize: '0.68rem', color: C.muted, marginTop: 2 }}>
          Drag from the base of the glass up to the fill level.
        </div>
      </div>

      {/* Readout: the count as it will be written — whole bottles kept,
          the slider owns the decimal. */}
      <div style={{ textAlign: 'center', flex: '0 0 auto', lineHeight: 1, padding: '2px 0 6px' }}>
        <span
          data-bottle-readout="1"
          style={{
            fontSize: '2.6rem', fontWeight: 900, color: C.ink,
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          {displayValue}
        </span>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: C.muted, marginLeft: 6 }}>
          {unitLabel}{displayValue === '1' ? '' : 's'}
        </span>
      </div>

      {/* Stage: the bottle + both rails. The wrapper is exactly the bottle
          box so rail ticks and the fill share one coordinate frame. */}
      <div
        ref={stageRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          ref={wrapRef}
          style={{ position: 'relative', width: bottlePx.w, height: bottlePx.h }}
        >
          {/* The gray fluid, masked to the inside of the glass. */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0,
              WebkitMaskImage: `url(${MASK_URL})`,
              maskImage: `url(${MASK_URL})`,
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          >
            <div
              data-bottle-fill="1"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${fillTopPct}%`,
                bottom: `${(1 - BODY_BOTTOM_FRAC) * 100}%`,
                background: `linear-gradient(180deg, ${C.fluidTop} 0%, ${C.fluidBottom} 100%)`,
                borderTop: `2px solid ${C.ink}`,
                transition: dragging ? 'none' : 'top 140ms ease-out',
              }}
            />
          </div>

          {/* The level line "hooks through" the bottle: it runs rail to rail,
              under the outline strokes, level with the fluid surface. */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -(RAIL_W + 2),
              right: -(RAIL_W + 2),
              top: `${fillTopPct}%`,
              height: 2,
              backgroundColor: 'rgba(90, 74, 66, 0.55)',
              transition: dragging ? 'none' : 'top 140ms ease-out',
            }}
          >
            <span style={{
              position: 'absolute', left: -6, top: '50%', transform: 'translateY(-50%)',
              width: 14, height: 14, borderRadius: '50%',
              backgroundColor: '#FFFFFF', border: `3px solid ${C.ink}`,
              boxSizing: 'border-box',
            }} />
            <span style={{
              position: 'absolute', right: -6, top: '50%', transform: 'translateY(-50%)',
              width: 14, height: 14, borderRadius: '50%',
              backgroundColor: '#FFFFFF', border: `3px solid ${C.ink}`,
              boxSizing: 'border-box',
            }} />
          </div>

          {/* The bottle itself, strokes always on top. */}
          <img
            src={OUTLINE_URL}
            alt=""
            draggable={false}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              pointerEvents: 'none', userSelect: 'none',
            }}
          />

          {rail('left')}
          {rail('right')}

          {/* Gesture surface: drag anywhere over bottle or rails. */}
          <div
            ref={sliderRef}
            role="slider"
            tabIndex={0}
            aria-label="Bottle fill level in tenths"
            aria-valuemin={0}
            aria-valuemax={BOTTLE_TENTHS_MAX}
            aria-valuenow={tenths}
            aria-valuetext={`${(tenths / 10).toFixed(1)} of a ${unitLabel}`}
            data-bottle-slider="1"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: -(RAIL_W + RAIL_GAP),
              right: -(RAIL_W + RAIL_GAP),
              cursor: 'ns-resize',
              touchAction: 'none',
              outline: 'none',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLostPointerCapture={endDrag}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      {/* Actions */}
      <div style={{
        flex: '0 0 auto',
        display: 'flex',
        gap: 10,
        padding: mobile
          ? '10px 18px calc(14px + env(safe-area-inset-bottom, 0px))'
          : '12px 20px 16px',
      }}>
        <button
          type="button"
          onClick={handleCancel}
          style={{
            flex: '0 0 auto',
            padding: '0 18px',
            minHeight: 52,
            borderRadius: 12,
            border: `2px solid ${C.line}`,
            backgroundColor: '#FFFFFF',
            color: C.body,
            fontSize: '0.95rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-bottle-done="1"
          onClick={handleDone}
          style={{
            flex: '1 1 auto',
            minHeight: 52,
            borderRadius: 12,
            border: 'none',
            backgroundColor: C.ink,
            color: '#FFFFFF',
            fontSize: '1.05rem',
            fontWeight: 800,
            fontFamily: 'Montserrat, sans-serif',
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      <style>{FLIP_CSS}</style>
      <div
        data-bottle-count-overlay="1"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10080,
          backgroundColor: 'rgba(38, 25, 18, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          perspective: '1400px',
        }}
        onClick={handleCancel}
      >
        {panel}
      </div>
    </>,
    document.body,
  );
}
